import { type ChangeEvent, useCallback } from 'react';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import { useController } from 'react-hook-form';
import { z } from 'zod/v4';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { PlainIconButton } from '@interxfi/design-system/components/button';
import { PasswordInput, type PasswordInputProps } from '@interxfi/design-system/components/input';
import { Typography } from '@interxfi/design-system/components/typography';
import CheckSmallIcon from '@interxfi/design-system/icons/react/icon-check-small';
import InfoIcon from '@interxfi/design-system/icons/react/icon-info';
import XIcon from '@interxfi/design-system/icons/react/icon-x';
import type { AppPasswordInputPropsWithLens } from '../../../../components/form/app-password-input';
import styles from './userauth-password-input.module.scss';

// Backend checks these with string contains, so we should do the same here to avoid any discrepancies.
const UPPERCASE_CHARACTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ' as const;
const LOWERCASE_CHARACTERS = 'abcdefghijklmnopqrstuvwxyz' as const;
const NUMERIC_CHARACTERS = '0123456789' as const;
const SPECIAL_CHARACTERS = '!@#$%^&*()-_=+[]{}|;:,.<>?/`~' as const;
const MIN_PASSWORD_LENGTH = 8;

export enum PasswordRequirementType {
  MinLength = 'minLength',
  Uppercase = 'uppercase',
  Lowercase = 'lowercase',
  Numeric = 'numeric',
  SpecialCharacter = 'specialCharacter',
}

function checkPasswordUppercase(value: string): boolean {
  return value.split('').some((char) => UPPERCASE_CHARACTERS.includes(char));
}

function checkPasswordLowercase(value: string): boolean {
  return value.split('').some((char) => LOWERCASE_CHARACTERS.includes(char));
}

function checkPasswordNumeric(value: string): boolean {
  return value.split('').some((char) => NUMERIC_CHARACTERS.includes(char));
}

function checkPasswordSpecialCharacter(value: string): boolean {
  return value.split('').some((char) => SPECIAL_CHARACTERS.includes(char));
}

export const userauthPasswordValidator = z
  .string()
  .min(MIN_PASSWORD_LENGTH, { message: PasswordRequirementType.MinLength })
  .refine(checkPasswordUppercase, { message: PasswordRequirementType.Uppercase })
  .refine(checkPasswordLowercase, { message: PasswordRequirementType.Lowercase })
  .refine(checkPasswordNumeric, { message: PasswordRequirementType.Numeric })
  .refine(checkPasswordSpecialCharacter, { message: PasswordRequirementType.SpecialCharacter });

interface PasswordRequirement {
  id: PasswordRequirementType;
  check: (value: string) => boolean;
  label: string;
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    id: PasswordRequirementType.MinLength,
    check: (value) => value.length >= MIN_PASSWORD_LENGTH,
    label: `At least ${MIN_PASSWORD_LENGTH} characters long`,
  },
  {
    id: PasswordRequirementType.Numeric,
    check: (value) => value.split('').some((char) => NUMERIC_CHARACTERS.includes(char)),
    label: 'Contains at least one number (0-9)',
  },
  {
    id: PasswordRequirementType.Uppercase,
    check: (value) => value.split('').some((char) => UPPERCASE_CHARACTERS.includes(char)),
    label: 'Contains at least one uppercase letter (A-Z)',
  },
  {
    id: PasswordRequirementType.Lowercase,
    check: (value) => value.split('').some((char) => LOWERCASE_CHARACTERS.includes(char)),
    label: 'Contains at least one lowercase letter (a-z)',
  },
  {
    id: PasswordRequirementType.SpecialCharacter,
    check: (value) => value.split('').some((char) => SPECIAL_CHARACTERS.includes(char)),
    label: `Contains at least one special character
(!@#$%^&*()-_=+[]{}|;:,.<>?/~\`)`,
  },
];

export function UserauthPasswordInput({ error: _, value, ...props }: PasswordInputProps) {
  // Don't pass the error here, it's handled manually
  const usableValue = value?.toString() ?? '';
  const meetsAll = PASSWORD_REQUIREMENTS.every((requirement) => requirement.check(usableValue));
  const infoButton = (
    <CollapsiblePrimitive.Trigger asChild>
      <PlainIconButton tone={meetsAll ? 'primary-dk' : 'danger-dk'}>
        <InfoIcon width={16} />
      </PlainIconButton>
    </CollapsiblePrimitive.Trigger>
  );

  return (
    <CollapsiblePrimitive.Root asChild>
      <VerticalStack className={styles.passwordInput} gap={4}>
        <PasswordInput
          appearance={usableValue ? (meetsAll ? 'default' : 'error') : 'default'}
          error={
            meetsAll ? undefined : (
              <HorizontalStack align="space-between" blockAlign="center" className={styles.hintContainer} gap={2}>
                <span>Fails requirements</span>
                {infoButton}
              </HorizontalStack>
            )
          }
          hint={
            meetsAll ? (
              <HorizontalStack align="space-between" blockAlign="center" className={styles.hintContainer} gap={2}>
                <span>Good</span>
                {infoButton}
              </HorizontalStack>
            ) : undefined
          }
          hintVariant="primary"
          {...props}
          value={value}
        />

        <CollapsiblePrimitive.Content asChild>
          <VerticalStack as="ul" className={styles.requirementList} gap={2}>
            {PASSWORD_REQUIREMENTS.map((requirement) => {
              const isMet = requirement.check(usableValue);

              return (
                <HorizontalStack as="li" blockAlign="start" gap={2} key={requirement.id} wrap="nowrap">
                  <Typography className={styles.indicator} tone={isMet ? 'green-dk' : 'danger-dk'}>
                    {isMet ? <CheckSmallIcon width={16} /> : <XIcon width={16} />}
                  </Typography>

                  <Typography lineHeight="1rem" size="0.75rem" tone={isMet ? 'md' : 'danger-dk'} wrap="preserve">
                    {requirement.label}
                  </Typography>
                </HorizontalStack>
              );
            })}
          </VerticalStack>
        </CollapsiblePrimitive.Content>
      </VerticalStack>
    </CollapsiblePrimitive.Root>
  );
}

export function UserauthPasswordInputWithLens({ error: _, lens, onChange, ...props }: AppPasswordInputPropsWithLens) {
  const { field, fieldState } = useController({ ...lens.interop() });
  const { onChange: fieldOnChange } = field;
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      fieldOnChange(e.target.value);
      onChange?.(e);
    },
    [onChange, fieldOnChange],
  );

  return (
    <UserauthPasswordInput
      {...props}
      {...field}
      error={fieldState.error?.message}
      onChange={handleChange}
      touched={fieldState.isTouched}
      value={field.value || ''}
    />
  );
}
